import { Modal } from "@components/modals";
import React from "react";
import { InstallationLogsDataType } from "../types";
import { Flex } from "@components/ui/Flex";
import styles from '../../styles.module.scss';
import { isEmpty } from "lodash";

type InstallationLogsModalProps = {
  nodeName: string|undefined;
  isVisible: boolean;
  onClose: () => void;
  logsData: InstallationLogsDataType[]
  currentPatchVersion: string
}

const InstallationLogsModal: React.FC<InstallationLogsModalProps> = ({ isVisible, onClose, logsData, nodeName, currentPatchVersion }) => {
  const getStatusBadge = (state: string) => {
    const getStatusClass = (status: string) => {
      const statusMap: { [key: string]: string } = {
        'NEW': 'new',
        'SUBMITTED': 'submitted',
        'QUEUED': 'queued',
        'RUNNING': 'running',
        'FAILED': 'failed',
        'STOPPING': 'stopping',
        'STOPPED': 'stopped',
        'SUCCESS': 'success',
        'RETRYING': 'retrying',
        'STAGED': 'staged',
        'LANDED': 'landed',
        'LANDING_FAILED': 'landingFailed',
        'NO_DATA': 'noData'
      };
      return statusMap[status] || 'new';
    };

    return (
      <span className={`${styles.statusBadge} ${styles[getStatusClass(state)]}`}>
        {state}
      </span>
    );
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('en-US', {
      month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  };

  const extractCommand = (requestBody: string) => {
    try {
      return JSON.parse(requestBody).command || 'N/A';
    } catch { return 'N/A'; }
  };

  return (
    <Modal
      isOpen={isVisible}
      toggleClose={onClose}
      title={
        <Flex display={"flex"}  flexDirection={"column"} gap={"12px"} >
          <Flex><span>Installation Logs - <span className={styles.titleNode}>{nodeName}</span></span></Flex>
          <div className={styles.gridItem}>
            <div className={styles.gridItemLabel}>📦 Current Version</div>
            <div className={styles.gridItemValue}>
              {currentPatchVersion || 'N/A'}
            </div>
          </div>
        </Flex>
      }
      subtitle={<Flex marginTop={"8px"}>{logsData.length} log entries</Flex>}
      className={styles.installtionLogsModal__containerExtraLarge}
      showCloseMark
    >
      <>
        <div className={`log_wrapper ${styles.logWrapper}`}>
          {isEmpty(logsData) ? (
            <div className={styles.noLogsContainer}>
              📋 No installation logs available
            </div>
          ) : (
            <div className={styles.logsContainer}>
              {logsData.map((log, index) => (
                <div
                  key={`${log.requestId}-${index}`}
                  className={styles.logItem}
                >
                  <div className={styles.logHeader}>
                    <div className={styles.requestIdContainer}>
                      <span>Request ID: {log.requestId}</span>
                      {getStatusBadge(log.state)}
                    </div>
                    {log.created && (
                      <div className={styles.createdOnContainer}>
                        🕒 Created On: {formatTimestamp(log.created)}
                      </div>
                    )}
                  </div>
                  <div className={styles.contentGrid}>
                    <div className={styles.gridItem}>
                      <div className={styles.gridItemLabel}>⚡ Command</div>
                      <div className={`${styles.gridItemValue} ${styles.monospace}`}>
                        {extractCommand(log.requestBody)}
                      </div>
                    </div>

                    <div className={`${styles.gridItem} ${styles.updateTo}`}>
                      <div className={styles.gridItemLabel}>🚀 Update To</div>
                      <div className={`${styles.gridItemValue} ${styles.updateToValue}`}>
                        {log.updatedToVersion || 'N/A'}
                      </div>
                    </div>


                  </div>

                  {log.errorLog && log.errorLog.length > 0 && (
                    <div className={styles.errorLogsSection}>
                      <div className={styles.errorLogsHeader}>
                        🚨 Error Logs ({log.errorLog.length})
                      </div>
                      <div className={styles.errorLogsContainer}>
                        {log.errorLog.map((error, errorIndex) => (
                          <div key={errorIndex} className={styles.errorLogItem}>
                            {error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}


                </div>
              ))}
            </div>
          )}
        </div>
      </>

      <div className="btns__box">
        <Flex gap={"16px"} justifyContent={"flex-end"} alignItems={"center"} marginTop={"20px"}>
          {!isEmpty(logsData) && <button
            className="btn-md btn-grey-transparent"
            onClick={() => {
              const dataStr = JSON.stringify(logsData, null, 2);
              const dataBlob = new Blob([dataStr], { type: 'application/json' });
              const url = URL.createObjectURL(dataBlob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `installation_logs-${nodeName}-${new Date().getTime()}.log`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);
            }}
          >
            <img
              src="/icons/workflow/download_code.svg"
              alt=""
              width="12"
              height="12"
            />&nbsp; Download
          </button>}
          <button
            className="btn-md btn-grey-transparent"
            type="button"
            onClick={onClose}
          >
            Close
          </button>
        </Flex>
      </div>
    </Modal>
  );
};

export default InstallationLogsModal;
